import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formErrorsSchema } from '@/common/api/schemas';
import { DeviceService } from '@/features/devices/services/DeviceService';
import { deviceQueryKeys } from '@/features/devices/queries';
import { buildParameterValueSchema } from '@/features/devices/utils/buildParameterValueSchema';
import type { DeviceParameterDefinition } from '@/features/devices/services/schemas';
import type { ParamSaveStatus } from '@/features/devices/components/parameter-field/ParameterField';

interface UseDeviceParametersOptions {
  deviceId: number | null;
  existingParameterIds?: number[];
  initialValues?: Record<number, string>;
  onSuccess?: (savedCount: number) => void | Promise<void>;
}

export function useDeviceParameters(
  definitions: DeviceParameterDefinition[],
  options: UseDeviceParametersOptions
) {
  const queryClient = useQueryClient();

  const [values, setValues] = useState<Record<number, string>>(() => {
    const vals: Record<number, string> = {};
    for (const def of definitions) vals[def.id] = def.value ?? '';
    for (const [id, val] of Object.entries(options.initialValues ?? {})) {
      vals[Number(id)] = val;
    }
    return vals;
  });

  const [statuses, setStatuses] = useState<Record<number, ParamSaveStatus>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  function getFieldProps(def: DeviceParameterDefinition) {
    return {
      value: values[def.id] ?? '',
      status: (statuses[def.id] ?? 'idle') as ParamSaveStatus,
      errorMessage: errors[def.id],
      onChange: (val: string) => {
        setValues((prev) => ({ ...prev, [def.id]: val }));
        setStatuses((prev) => (prev[def.id] !== 'pending' ? { ...prev, [def.id]: 'idle' } : prev));
        setErrors((prev) => {
          if (!prev[def.id]) return prev;
          const next = { ...prev };
          delete next[def.id];
          return next;
        });
      },
    };
  }

  function reset(newValues: Record<number, string>) {
    setValues(newValues);
    setStatuses({});
    setErrors({});
  }

  async function save() {
    if (options.deviceId === null) return;
    const deviceId = options.deviceId;

    setIsSaving(true);

    const savedIds = new Set(options.existingParameterIds ?? []);
    const toSave = definitions.filter((def) => statuses[def.id] !== 'success');
    let anyError = false;
    let savedCount = 0;

    for (const def of toSave) {
      const defId = def.id;

      const validation = buildParameterValueSchema(def).safeParse(values[defId] ?? '');
      if (!validation.success) {
        const msg = validation.error.issues[0]?.message ?? t`Invalid value`;
        setStatuses((prev) => ({ ...prev, [defId]: 'error' }));
        setErrors((prev) => ({ ...prev, [defId]: msg }));
        anyError = true;
        continue;
      }

      setStatuses((prev) => ({ ...prev, [defId]: 'pending' }));
      try {
        if (savedIds.has(defId)) {
          await DeviceService.updateParameter(deviceId, defId, { value: values[defId] ?? '' });
        } else {
          await DeviceService.createParameter(deviceId, {
            parameterDefinition: defId,
            value: values[defId] ?? '',
          });
        }
        savedCount++;
        setStatuses((prev) => ({ ...prev, [defId]: 'success' }));
        setErrors((prev) => {
          const next = { ...prev };
          delete next[defId];
          return next;
        });
      } catch (err) {
        anyError = true;
        const parsed = isAxiosError(err) && formErrorsSchema.safeParse(err.response?.data);
        const message =
          parsed && parsed.success
            ? (Object.values(parsed.data.errors).flat()[0] ?? parsed.data.message)
            : err instanceof Error
              ? err.message
              : t`Unexpected error`;
        setStatuses((prev) => ({ ...prev, [defId]: 'error' }));
        setErrors((prev) => ({ ...prev, [defId]: message }));
      }
    }

    setIsSaving(false);

    if (savedCount > 0) {
      await queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });
    }

    if (anyError) {
      toast.error(t`Some parameters failed to save`);
    } else {
      await options.onSuccess?.(savedCount);
    }
  }

  return { statuses, errors, isSaving, getFieldProps, save, reset };
}
